import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Box, Button, CircularProgress } from "@mui/material";
import Login from "./login";
import TermsAndConditions from "./TermsAndConditions";
import VerificationCode from "./VerificationCode";
import SerialNumberCheck from "./SerialNumberCheck";
import { loginRequestCaps } from "../authConfig";
import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import gmlogo from "./GMEnergy.png";
import "./styles.css";
import { API_ENDPOINT_DEV, API_ENDPOINT_TEST, API_ENDPOINT_PROD } from "../utils/constants.ts";

const { host: _host } = window.location;
const API_BASE = (() => {
  if (_host.includes("prod")) return API_ENDPOINT_PROD;
  if (_host.includes("tst"))  return API_ENDPOINT_TEST;
  return API_ENDPOINT_DEV;
})();

const retryStyles = {
  errorTextTop: { paddingBottom: "10px", fontSize: "14px", fontWeight: "normal", color: "red" },
  errorTextBottom: { paddingBottom: "20px", fontSize: "14px", fontWeight: "normal", color: "red" },
};

const MainPage = () => {
  const [savedOrderNumber, setSavedOrderNumber] = useState(-1);
  const [savedJobType, setSavedJobType] = useState("");
  const [savedSerialNumber, setSavedSerialNumber] = useState("");
  const [showLogin, setShowLogin] = useState(true);
  const [showSerialNumberCheck, setShowSerialNumberCheck] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const [token, setToken] = useState(null);
  const [errorOccurred, setErrorOccurred] = useState(false);
  const [serialError, setSerialError] = useState(false);
  const { instance, accounts, inProgress } = useMsal();
  const [passcode, setPasscode] = useState("");
  const [loginError, setLoginError] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  const reacquireToken = useCallback(() => {
    const request = {
      scopes: ["openid"],
      account: instance.getAllAccounts()[0],
    };

    instance
      .acquireTokenSilent(request)
      .then((tokenResponse) => {
        setToken(tokenResponse.idToken);
        setShowLogin(false);
        setShowTerms(true);
      })
      .catch((error) => {
        console.log("error", error);
      });
  }, [instance]);

  // MsalProvider handles initialize() and handleRedirectPromise() for us.
  // We wait until no interaction is in progress, then read the resulting
  // account state. After a redirect login the account is cached, so we can
  // silently acquire the token; otherwise we show the login screen.
  useEffect(() => {
    if (inProgress !== InteractionStatus.None) {
      return;
    }
    if (accounts.length > 0) {
      reacquireToken();
    } else {
      setShowLogin(true);
      setShowTerms(false);
    }
  }, [inProgress, accounts, reacquireToken]);

  const setTnCAndOrderNumber = async (TermNCond, OrderNum, serialNum, JobTy) => {
    const config = {
      headers: {
        caps_auth: token,
        "Content-Type": "application/json",
      },
    };
    const data = {
      TnC: TermNCond,
      OrderNumber: OrderNum,
      SerialNumber: serialNum,
      JobType: JobTy,
    };

    try {
      const api = `${API_BASE}/api/generateordernumber`;
      const response = await axios.post(api, data, config);
      console.log(
        "TnC, Job Type, and OrderNumber set successfully:",
        response.data
      );
      console.log("response.data.Passcode", response.data.Passcode);
      if (response?.data?.Passcode) {
        setPasscode(response.data.Passcode);
        setErrorOccurred(false);
      }
    } catch (error) {
      setErrorOccurred(true);
      console.log("Failed to set TnC, Job Type, and OrderNumber:", error);
      if (error.response?.data?.field === "Token has expired") {
        handleSignOut();
      }
    }
  };

  const checkSerialNumber = async (serialNumber) => {
    const endpoint = "/api/customers/tnc-acceptance";
    const config = {
      headers: {
        caps_auth: token,
        "Content-Type": "application/json",
      },
    };
    const data = {
      serial_number: serialNumber,
    };

    try {
      const api = `${API_BASE}${endpoint}`;
      const response = await axios.post(api, data, config);
      return {
        status: response.status,
        message: response.data.RespMess || "No message",
      };
    } catch (error) {
      if (error.response) {
        return {
          status: error.response.status,
          message: error.response.data.message || "No message",
        };
      } else {
        return { status: 500, message: error.message };
      }
    }
  };

  const handleSignIn = () => {
    if (inProgress !== InteractionStatus.None) {
      return;
    }
    setLoginError(false);
    setIsLoggingIn(true);
    instance.loginRedirect(loginRequestCaps).catch(() => {
      setLoginError(true);
      setIsLoggingIn(false);
    });
  };

  const handleSignOut = () => {
    instance.logoutRedirect({
      postLogoutRedirectUri: "/",
    });
  };

  const handleTermsAccept = async (orderNumber, jobType, serialNumber) => {
    setShowTerms(false);
    setTnCAndOrderNumber(true, orderNumber || -1, serialNumber, jobType);
    setSavedOrderNumber(orderNumber || -1);
    setSavedJobType(jobType);
    setSavedSerialNumber(serialNumber);
    const { status } = await checkSerialNumber(serialNumber);
    if (status === 200) {
      setShowVerification(true);
      setSerialError(false);
    } else if (status === 400) {
      setShowSerialNumberCheck(true);
      setSerialError(false);
    } else {
      setShowTerms(true);
      setSerialError(true);
    }
  };

  const handleRetry = () => {
    setTnCAndOrderNumber(true, savedOrderNumber, savedSerialNumber, savedJobType);
  };

  const handleSerialNumberCheckAccept = async () => {
    const { status } = await checkSerialNumber(savedSerialNumber);
    if (status === 200) {
      setShowSerialNumberCheck(false);
      setShowVerification(true);
    } else {
      setShowSerialNumberCheck(true);
    }
  };

  const handleVerification = () => {
    setShowVerification(false);
  };

  const isPostLoginLoading =
    inProgress !== InteractionStatus.None ||
    (accounts.length > 0 && showLogin);

  return (
    <Box
      sx={{
        backgroundColor: "#2e2d31",
        minHeight: "100vh",
        position: "relative",
        overflow: "auto",
        overflowX: "hidden",
      }}
    >
      {isPostLoginLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
          <CircularProgress color="primary" />
        </Box>
      ) : (
      <div className="page-container">
        <div className="pages">
          {showLogin && <Login onLogin={handleSignIn} loginError={loginError} loading={isLoggingIn} />}
          {showSerialNumberCheck && (
            <SerialNumberCheck onAccept={handleSerialNumberCheckAccept} />
          )}
          {showTerms && (
            <TermsAndConditions
              onAccept={handleTermsAccept}
              serialError={serialError}
              setSerialError={setSerialError}
            />
          )}
          {showVerification && (
            <VerificationCode
              onRetry={handleRetry}
              onSuccess={handleVerification}
              passcode={passcode}
            />
          )}
        </div>
        {errorOccurred && showVerification && (
          <div className="retry" style={{ maxWidth: "500px" }}>
            <div style={retryStyles.errorTextTop}>
              An error occurred. Click below to regenerate the code.
            </div>
            <div style={retryStyles.errorTextBottom}>
              If you continue to get this error, please call the GM Energy
              Customer Support Center at{" "}
              <span style={{ whiteSpace: "nowrap" }}>1-833-64POWER</span>
            </div>
            <Button
              variant="contained"
              color="primary"
              size="small"
              style={{ fontSize: "12px" }}
              onClick={handleRetry}
            >
              Regenerate Code
            </Button>
          </div>
        )}
      </div>
      )}
      {token && (
        <Button
          variant="contained"
          color="primary"
          onClick={handleSignOut}
          sx={{
            position: "absolute",
            top: "50px",
            right: "50px",
            zIndex: "1",
            "@media (max-width: 325px)": {
              top: "100px",
              left: "50px",
              width: "125px",
            },
          }}
        >
          Logout
        </Button>
      )}
      <img
        src={gmlogo}
        alt="GM Energy"
        style={{
          position: "absolute",
          top: "45px",
          height: "50px",
          left: "50px",
          zIndex: "0",
          filter: "brightness(0) invert(1)",
        }}
      />
    </Box>
  );
};

export default MainPage;
